import { useCallback, useMemo, useState } from 'react'
import { storeFutuWSMarket } from './useTickerStore'

export interface TopMenuMarketList {
  id: string
  symbol: string
  vol: string
  last: string
  change: string
  fundingRate?: string
  countdown?: string
}

export interface useMarketListReturn {
  showMarketList: Map<string, TopMenuMarketList>
  searchTerm: string
  setSearchTerm: (term: string) => void

  clickOrder: (key: string) => void
  sortOfItem: string
  sortOfDesc: string
  setSortOfItem: (item: string) => void
  setSortOfDesc: (desc: string) => void
}

const sortKey = ['symbol', 'volume', 'last', 'changeRate']
const useTickerList = (): useMarketListReturn => {

  const { ticker24 } = storeFutuWSMarket()

  // 검색 기능
  const [searchTerm, setSearchTerm] = useState<string>('')

  // 정렬항목, 정렬 순서
  const [sortOfItem, setSortOfItem] = useState<string>('volume')
  const [sortOfDesc, setSortOfDesc] = useState<string>('') // asc, desc, ""
  const resetSort = () => {
    setSortOfItem('volume')
    setSortOfDesc('')
  }

  // 동일한 종목 클릭시 sort를 변경 ,
  // 다른 종목 클릭시 key를 변경
  const clickOrder = (key: string) => {
    if (sortOfItem === key) {
      if (sortOfDesc === '') {
        setSortOfDesc('asc')
      } else if (sortOfDesc === 'asc') {
        setSortOfDesc('desc')
      } else if (sortOfDesc === 'desc') {
        resetSort()
      }
    } else {
      setSortOfItem(key)
      setSortOfDesc('asc')
    }
  }

  // 초기화 작업
  const setInitTikcer = useCallback((args: Map<string, Binance24hrTicker | null> | null) => {
    const tmp = new Map<string, TopMenuMarketList>()

    if (args === null) {
      return tmp
    }

    const tickerList = args
    tickerList.forEach((value, key) => {
      if (value === null) {
        return
      }
      const customData: TopMenuMarketList = {
        id: `${value.s}-ticker`,
        symbol: key,
        vol: value?.q ?? '0',
        last: value?.c ?? '0',
        change: value?.P ?? '0',
      }
      tmp.set(key, customData)
    })
    return tmp
  }, [])

  // search 에 따라 필터링
  const filterSearch = (args: Map<string, TopMenuMarketList>, argsSearchTerm: string): Map<string, TopMenuMarketList> => {
    if (args.size === 0) {
      return args
    }

    // 빈값이면 패스~
    if (argsSearchTerm === '') {
      return args
    }
    // const tmp = new Map<string, TopMenuMarketList>()
    // args.forEach((value, key) => {
    //   if (key.includes(argsSearchTerm.toLocaleUpperCase())) {
    //     tmp.set(key, value)
    //   }
    // })
    const tmp = new Map(Array.from(args).filter(([key]) => key.includes(argsSearchTerm.toLocaleUpperCase())))
    return tmp
  }

  // 정렬에 따라 필터링
  const filterSort = useCallback(
    (args: Map<string, TopMenuMarketList>): Map<string, TopMenuMarketList> => {
      if (args.size === 0) {
        return args
      }

      // 정렬이 없다면 volume 정렬을 딜폴트로 한다.
      if (sortOfDesc === '') {
        const sortedEntries = Array.from(args.entries()).sort((a, b) => Number(b[1].vol) - Number(a[1].vol))
        const sortedTmp = new Map<string, TopMenuMarketList>(sortedEntries)
        return sortedTmp
      }

      // symbol 정렬
      const sortedBySymbol = (a: [string, TopMenuMarketList], b: [string, TopMenuMarketList]) => {
        if (sortOfDesc === 'asc') {
          return b[1].symbol.localeCompare(a[1].symbol)
        }

        if (sortOfDesc === 'desc') {
          return a[1].symbol.localeCompare(b[1].symbol)
        }
        return 0
      }

      // volume 정렬
      const sortedByVolume = (a: [string, TopMenuMarketList], b: [string, TopMenuMarketList]) => {
        if (sortOfDesc === 'asc') {
          return Number(b[1].vol) - Number(a[1].vol)
        }
        if (sortOfDesc === 'desc') {
          return Number(a[1].vol) - Number(b[1].vol)
        }

        return 0
      }

      // lastPrice 정령
      const sortedByLastPrice = (a: [string, TopMenuMarketList], b: [string, TopMenuMarketList]) => {
        if (sortOfDesc === 'asc') {
          return Number(b[1].last) - Number(a[1].last)
        }
        if (sortOfDesc === 'desc') {
          return Number(a[1].last) - Number(b[1].last)
        }
        return 0
      }

      // chagneRate 정령
      const sortedByChangeRate = (a: [string, TopMenuMarketList], b: [string, TopMenuMarketList]) => {
        if (sortOfDesc === 'asc') {
          return Number(b[1].change) - Number(a[1].change)
        }
        if (sortOfDesc === 'desc') {
          return Number(a[1].change) - Number(b[1].change)
        }

        return 0
      }

      const sortedTmp = new Map(
        Array.from(args.entries()).sort((a: [string, TopMenuMarketList], b: [string, TopMenuMarketList]) => {
          if (sortOfItem === sortKey[0]) {
            return sortedBySymbol(a, b)
          }
          if (sortOfItem === sortKey[1]) {
            return sortedByVolume(a, b)
          }
          if (sortOfItem === sortKey[2]) {
            return sortedByLastPrice(a, b)
          }
          if (sortOfItem === sortKey[3]) {
            return sortedByChangeRate(a, b)
          }
          return 0 // sortOfItem이 어떤 조건에도 맞지 않으면 변경되지 않도록 합니다.
        }),
      )

      return sortedTmp
    },
    [sortOfItem, sortOfDesc],
  )

  const showMarketList = useMemo(() => {
    let tmp = setInitTikcer(ticker24)
    if (searchTerm !== '') {
      // 검색어 없을때
      tmp = filterSearch(tmp, searchTerm)
    }

    tmp = filterSort(tmp as Map<string, TopMenuMarketList>)
    return tmp
  }, [ticker24, searchTerm, filterSort])

  return {
    showMarketList,
    searchTerm,
    setSearchTerm,
    clickOrder,
    sortOfItem,
    sortOfDesc,
    setSortOfItem,
    setSortOfDesc,
  }
}

export default useTickerList
